import { useState, useEffect } from "react";
import { apiFetch } from "../lib/api";

interface PlatformStatus {
  youtube: boolean;
  instagram: boolean;
  facebook: boolean;
  tiktok: boolean;
  xiaohongshu: boolean;
  linkedin: boolean;
  jev: boolean;
}

interface AnalystRunResult {
  totalCandidates: number;
  highValueCount: number;
  highValueCandidates: {
    platform: string;
    displayName: string;
    url?: string;
    jevScore?: {
      compositeScore: number;
      industry?: string;
    };
  }[];
  durationMs: number;
}

export default function AnalystPage() {
  const [status, setStatus] = useState<PlatformStatus | null>(null);
  const [query, setQuery] = useState("");
  const [platforms, setPlatforms] = useState<string[]>(["youtube", "instagram"]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AnalystRunResult | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      const res = await apiFetch("/admin/analyst/status");
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error("Failed to load analyst status:", err);
    }
  }

  async function handleRun() {
    if (!query.trim()) {
      setError("請輸入搜尋關鍵字");
      return;
    }
    setRunning(true);
    setError("");
    setResult(null);

    try {
      const res = await apiFetch("/admin/analyst/run", {
        method: "POST",
        body: JSON.stringify({
          query: query.trim(),
          platforms,
          icp: {
            industry: "food_beverage",
            targetType: "kol",
            targetArea: "hk",
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "執行失敗");
    } finally {
      setRunning(false);
    }
  }

  const platformList = [
    { id: "youtube", name: "YouTube", icon: "📺" },
    { id: "instagram", name: "Instagram", icon: "📷" },
    { id: "facebook", name: "Facebook", icon: "👍" },
    { id: "tiktok", name: "TikTok", icon: "🎵" },
    { id: "xiaohongshu", name: "小紅書", icon: "📕" },
    { id: "linkedin", name: "LinkedIn", icon: "💼" },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">🧠 AI 員工「分析員」</h1>
        <p className="text-sm text-slate-500 mt-1">
          幫商戶喺社交平台搵潛在客戶／合作對象——YouTube／Instagram／Facebook／TikTok／小紅書／LinkedIn
        </p>
      </div>

      {/* 平台狀態 */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 mb-6">
        <h2 className="text-lg font-semibold text-emerald-400 mb-4">🔌 平台連接狀態</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {platformList.map((p) => {
            const connected = status?.[p.id as keyof PlatformStatus] ?? false;
            return (
              <div
                key={p.id}
                className={`rounded-lg border p-3 text-center ${
                  connected
                    ? "bg-emerald-950/30 border-emerald-700/50"
                    : "bg-slate-950/40 border-slate-800"
                }`}
              >
                <div className="text-2xl mb-1">{p.icon}</div>
                <div className="text-sm font-medium text-slate-200">{p.name}</div>
                <div className={`text-xs mt-1 ${connected ? "text-emerald-400" : "text-slate-500"}`}>
                  {connected ? "已連接" : "未配置"}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 text-xs text-slate-500">
          Jev 評分引擎：{status?.jev ? "✅ 已連接" : "❌ 未配置"}
        </div>
      </div>

      {/* 執行分析 */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 mb-6">
        <h2 className="text-lg font-semibold text-emerald-400 mb-4">🚀 執行分析</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">搜尋關鍵字</label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="例如：hong kong food review、日式料理、香港甜品"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">搜尋平台</label>
            <div className="flex flex-wrap gap-2">
              {platformList.map((p) => {
                const connected = status?.[p.id as keyof PlatformStatus] ?? false;
                const selected = platforms.includes(p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => {
                      if (!connected) return;
                      setPlatforms((prev) =>
                        prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id]
                      );
                    }}
                    disabled={!connected}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      !connected
                        ? "bg-slate-950/40 border-slate-800 text-slate-600 cursor-not-allowed"
                        : selected
                          ? "bg-emerald-600/20 border-emerald-500 text-emerald-300"
                          : "bg-slate-950/40 border-slate-700 text-slate-400 hover:border-slate-500"
                    }`}
                  >
                    {p.icon} {p.name}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            onClick={handleRun}
            disabled={running || platforms.length === 0}
            className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg transition-colors"
          >
            {running ? "分析中…" : "開始分析"}
          </button>

          {error && (
            <div className="text-rose-400 text-sm bg-rose-950/30 border border-rose-800/50 rounded-lg p-3">
              ❌ {error}
            </div>
          )}
        </div>
      </div>

      {/* 結果 */}
      {result && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-emerald-400 mb-4">📊 分析結果</h2>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-slate-200">{result.totalCandidates}</div>
              <div className="text-xs text-slate-500 mt-1">總候選</div>
            </div>
            <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-emerald-400">{result.highValueCount}</div>
              <div className="text-xs text-slate-500 mt-1">高分候選</div>
            </div>
            <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-slate-200">{(result.durationMs / 1000).toFixed(1)}s</div>
              <div className="text-xs text-slate-500 mt-1">執行時間</div>
            </div>
          </div>

          {result.highValueCandidates.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-300">高分名單</h3>
              {result.highValueCandidates.map((c, i) => (
                <div
                  key={i}
                  className="bg-slate-950/40 border border-slate-800 rounded-lg p-4 flex items-start justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">
                        {c.platform === "youtube" ? "📺" : c.platform === "instagram" ? "📷" : "📱"}
                      </span>
                      <span className="font-medium text-slate-200">{c.displayName}</span>
                      <span className="text-xs text-slate-500">{c.platform}</span>
                    </div>
                    {c.jevScore && (
                      <div className="text-xs text-slate-400">
                        評分: {(c.jevScore.compositeScore * 100).toFixed(0)}%
                        {c.jevScore.industry && ` | 行業: ${c.jevScore.industry}`}
                      </div>
                    )}
                  </div>
                  {c.url && (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-400 hover:underline text-sm ml-4"
                    >
                      開啟 →
                    </a>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-slate-500 py-8">
              冇高分候選——試下調整關鍵字或平台
            </div>
          )}
        </div>
      )}
    </div>
  );
}
